import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../services/firebase';
import Topbar from '../components/Topbar';
import Icon from '../components/Icon';
import Loading from '../components/Loading';
import './ChannelsView.css';

export default function ChannelsView({ accountId, onBack, onLogout }) {
  const { t } = useTranslation();
  const [channels, setChannels] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [metaPhoneNumberId, setMetaPhoneNumberId] = useState('');
  const [promptId, setPromptId] = useState('atendimento');
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [ent, setEnt] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    httpsCallable(functions, 'getEntitlements')({ accountId })
      .then(res => setEnt(res.data))
      .catch(() => setEnt(null));
  }, [accountId]);

  const maxChannels = ent?.limits?.maxChannels ?? -1;
  const atLimit = maxChannels !== -1 && channels.length >= maxChannels;
  const inactive = ent && !ent.active;
  const blocked = atLimit || inactive;

  const fetchData = async () => {
    const [chSnap, prSnap] = await Promise.all([
      getDocs(collection(db, 'accounts', accountId, 'channels')),
      getDocs(collection(db, 'accounts', accountId, 'prompts')),
    ]);
    setChannels(chSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    const pr = prSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    setPrompts(pr);
    if (pr.length > 0 && !pr.find(p => p.id === promptId)) setPromptId(pr[0].id);
    setLoadingList(false);
  };

  useEffect(() => {
    fetchData();
  }, [accountId]);

  const handleConnect = async () => {
    if (!phoneNumber.trim() || !metaPhoneNumberId.trim()) return;
    setError('');
    setLoading(true);
    try {
      const connectChannel = httpsCallable(functions, 'connectChannel');
      await connectChannel({ accountId, name, phoneNumber, metaPhoneNumberId, promptId });
      setName('');
      setPhoneNumber('');
      setMetaPhoneNumberId('');
      await fetchData();
    } catch (err) {
      console.error(err);
      setError(t('channels.errors.connect'));
    } finally {
      setLoading(false);
    }
  };

  const changePrompt = async (channelId, newPromptId) => {
    await updateDoc(doc(db, 'accounts', accountId, 'channels', channelId), { promptId: newPromptId });
    await fetchData();
  };

  const renameChannel = async (channel) => {
    const novo = window.prompt(t('channels.renamePrompt'), channel.name || channel.phoneNumber);
    if (novo == null || !novo.trim()) return;
    await updateDoc(doc(db, 'accounts', accountId, 'channels', channel.id), { name: novo.trim() });
    await fetchData();
  };

  const deleteChannel = async (channel) => {
    if (!window.confirm(t('channels.deleteConfirm', { name: channel.name || channel.phoneNumber }))) return;
    try {
      const disconnectChannel = httpsCallable(functions, 'disconnectChannel');
      await disconnectChannel({ accountId, channelId: channel.id });
      await fetchData();
    } catch (err) {
      console.error(err);
      setError(t('channels.errors.delete'));
    }
  };

  return (
    <div className="page">
      <Topbar onBack={onBack} onLogout={onLogout} crumbs={[t('channels.crumb')]} />
      <main className="page-main">
        <div className="cv-head">
          <h1>{t('channels.title')}</h1>
          {ent && maxChannels !== -1 && (
            <p className="cv-count">{t('channels.count', { current: channels.length, max: maxChannels, plan: ent.limits.label })}</p>
          )}
        </div>

        {blocked && (
          <div className="cv-gate card">
            {inactive ? t('channels.gateInactive') : t('channels.gateLimit')}
          </div>
        )}

        <div className="card cv-form">
          <input className="field" placeholder={t('channels.namePlaceholder')} value={name} onChange={e => setName(e.target.value)} />
          <input className="field" placeholder={t('channels.phonePlaceholder')} value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} />
          <input className="field" placeholder={t('channels.metaIdPlaceholder')} value={metaPhoneNumberId} onChange={e => setMetaPhoneNumberId(e.target.value)} />
          <select className="field" value={promptId} onChange={e => setPromptId(e.target.value)}>
            {prompts.map(p => <option key={p.id} value={p.id}>{p.name || p.id}</option>)}
          </select>
          <button className="btn btn-primary" onClick={handleConnect} disabled={loading || blocked}>
            <Icon name="plus" size={17} />{loading ? t('channels.connecting') : t('channels.connect')}
          </button>
          {error && <p className="form-error">{error}</p>}
        </div>

        {loadingList ? (
          <div className="card"><Loading label={t('channels.loading')} /></div>
        ) : channels.length === 0 ? (
          <div className="cv-empty card">{t('channels.empty')}</div>
        ) : (
          <div className="cv-list">
            {channels.map(c => (
              <div className="card cv-item" key={c.id}>
                <span className="cv-ic"><Icon name="phone" size={20} /></span>
                <div className="cv-info">
                  <div className="cv-number">{c.name || c.phoneNumber}</div>
                  <div className="meta">{c.phoneNumber} · id: {c.metaPhoneNumberId}</div>
                </div>
                <select className="field cv-prompt" value={c.promptId || 'atendimento'} onChange={e => changePrompt(c.id, e.target.value)} title={t('channels.channelPromptTitle')}>
                  {prompts.map(p => <option key={p.id} value={p.id}>{p.name || p.id}</option>)}
                </select>
                <div className="cv-row-actions">
                  <button className="btn btn-ghost btn-sm" onClick={() => renameChannel(c)} aria-label={t('common.rename')}>
                    <Icon name="edit" size={15} />
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => deleteChannel(c)} aria-label={t('common.delete')}>
                    <Icon name="trash" size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
